import { useQuery } from '@apollo/client';
import {
  DependencyGraph,
  DependencyGraphTypes,
  ErrorPanel,
  Progress,
} from '@backstage/core-components';
import { useTheme } from '@material-ui/core';
import { useContext } from 'react';

import { SystemDependenciesEntities } from '@agilelab/plugin-wb-marketplace-common';
import { WbCard, WbButton, WbCardContent } from '@agilelab/plugin-wb-platform';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router';
import { GET_DATA_PRODUCT_INSTANCE_DEPENDENCIES_BY_ID_AND_ENV_NAME } from '../../graphql';
import { SystemContext } from '../../hooks/DataProductContext';
import { discoveryZoomInRouteRef } from '../../routes';
import { useDependenciesStyle } from '../../themes';
import { parseDependenciesNodes } from '../../utils';
import { RenderedNode } from './RenderedNode';

export const DependenciesCard = () => {
  const dpInstance = useContext(SystemContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const discoveryZoomInRoute = useRouteRef(discoveryZoomInRouteRef);

  const classes = useDependenciesStyle();
  const systemNodes = new Array<{
    id: string;
    kind: string;
    name: string;
    domain: string;
    dataProductInstanceId?: string;
  }>();
  const systemEdges = new Array<{
    from: string;
    to: string;
    label: string;
  }>();
  const { loading, error, data } = useQuery<SystemDependenciesEntities>(
    GET_DATA_PRODUCT_INSTANCE_DEPENDENCIES_BY_ID_AND_ENV_NAME,
    {
      variables: {
        id: dpInstance.id,
        envName: dpInstance.environment.name,
      },
    },
  );

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <WbCard title="Dependencies">
        <WbCardContent>
          <ErrorPanel error={error} />
        </WbCardContent>
      </WbCard>
    );
  }

  const openZoomInHandler = () => {
    const url = discoveryZoomInRoute({ id: dpInstance.id.toString() });
    navigate(url);
  };

  const dependenciesNodes = parseDependenciesNodes(data);
  dependenciesNodes.forEach(dp => {
    systemNodes.push({
      id: dp.id.toString(),
      name: dp.displayName,
      kind: dp.type,
      domain: dp.domain,
      dataProductInstanceId: dp.id.dataProductInstanceId,
    });
    dp.inputIds?.forEach(link =>
      systemEdges.push({
        to: dp.id.toString(),
        from: link.toString(),
        label: '',
      }),
    );
  });

  return (
    <WbCard
      title="Dependencies"
      actions={
        <WbButton
          variant="outlined"
          label="Open zoom in"
          onClick={openZoomInHandler}
        />
      }
    >
      <WbCardContent>
        <DependencyGraph
          nodes={systemNodes}
          edges={systemEdges}
          nodeMargin={10}
          direction={DependencyGraphTypes.Direction.LEFT_RIGHT}
          renderNode={props => <RenderedNode {...props} />}
          paddingX={theme.spacing(4)}
          paddingY={theme.spacing(4)}
          className={classes.graph}
        />
      </WbCardContent>
    </WbCard>
  );
};
